import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  getDocs, 
  onSnapshot,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Document Edit Service
 * 
 * Manages document edit sessions, version control, and collaboration features
 * for enhanced desktop Office workflow without expensive Microsoft licensing.
 */

/**
 * Create a new edit session when user starts editing a document
 * @param {string} documentId - Document ID
 * @param {string} userId - User ID who is editing
 * @param {Object} userInfo - User information (name, email)
 * @returns {Promise<string>} - Edit session ID
 */
export const createEditSession = async (documentId, userId, userInfo) => {
  try {
    console.log('📝 Creating edit session for document:', documentId, 'user:', userInfo.name);
    
    // Check if user already has an active session for this document
    const existingSessionQuery = query(
      collection(db, 'c3_document_edit_sessions'),
      where('documentId', '==', documentId),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );
    const existingSessions = await getDocs(existingSessionQuery);
    
    // Close any existing sessions for this user/document
    for (const sessionDoc of existingSessions.docs) {
      await updateDoc(sessionDoc.ref, {
        status: 'completed',
        endedAt: serverTimestamp(),
        endReason: 'new_session_started'
      });
    }
    
    // Create new edit session
    const sessionData = {
      documentId,
      userId,
      userName: userInfo.name || userInfo.firstName + ' ' + userInfo.lastName,
      userEmail: userInfo.email,
      status: 'active', // active, completed, abandoned
      startedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      downloadedAt: serverTimestamp(),
      uploadedAt: null,
      endedAt: null,
      endReason: null, // completed, abandoned, timeout, new_session_started
      sessionType: 'desktop_edit',
      metadata: {
        userAgent: navigator.userAgent,
        platform: navigator.platform
      }
    };
    
    const sessionRef = await addDoc(collection(db, 'c3_document_edit_sessions'), sessionData);
    console.log('✅ Edit session created:', sessionRef.id);
    
    return sessionRef.id;
  } catch (error) {
    console.error('❌ Error creating edit session:', error);
    throw error;
  }
};

/**
 * Update edit session activity (keep session alive)
 * @param {string} sessionId - Edit session ID
 * @param {Object} updates - Updates to apply
 */
export const updateEditSession = async (sessionId, updates) => {
  try {
    const sessionRef = doc(db, 'c3_document_edit_sessions', sessionId);
    await updateDoc(sessionRef, {
      ...updates,
      lastActivityAt: serverTimestamp()
    });
    console.log('🔄 Edit session updated:', sessionId);
  } catch (error) {
    console.error('❌ Error updating edit session:', error);
    throw error;
  }
};

/**
 * Complete an edit session
 * @param {string} sessionId - Edit session ID
 * @param {string} endReason - Reason for ending (completed, abandoned, timeout)
 * @param {Object} completionData - Additional completion data
 */
export const completeEditSession = async (sessionId, endReason = 'completed', completionData = {}) => {
  try {
    const sessionRef = doc(db, 'c3_document_edit_sessions', sessionId);
    await updateDoc(sessionRef, {
      status: 'completed',
      endedAt: serverTimestamp(),
      endReason,
      ...completionData
    });
    console.log('✅ Edit session completed:', sessionId, 'reason:', endReason);
  } catch (error) {
    console.error('❌ Error completing edit session:', error);
    throw error;
  }
};

/**
 * Get active edit sessions for a document
 * @param {string} documentId - Document ID
 * @returns {Promise<Array>} - Array of active edit sessions
 */
export const getActiveEditSessions = async (documentId) => {
  try {
    const sessionsQuery = query(
      collection(db, 'c3_document_edit_sessions'),
      where('documentId', '==', documentId),
      where('status', '==', 'active'),
      orderBy('startedAt', 'desc')
    );
    
    const sessionsSnapshot = await getDocs(sessionsQuery);
    const sessions = sessionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter out stale sessions (older than 4 hours)
    const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000);
    const activeSessions = sessions.filter(session => {
      const lastActivity = session.lastActivityAt?.toDate() || session.startedAt?.toDate();
      return lastActivity && lastActivity > fourHoursAgo;
    });
    
    // Auto-cleanup stale sessions
    const staleSessions = sessions.filter(session => {
      const lastActivity = session.lastActivityAt?.toDate() || session.startedAt?.toDate();
      return !lastActivity || lastActivity <= fourHoursAgo;
    });
    
    for (const staleSession of staleSessions) {
      await completeEditSession(staleSession.id, 'timeout');
    }
    
    return activeSessions;
  } catch (error) {
    console.error('❌ Error getting active edit sessions:', error);
    return [];
  }
};

/**
 * Listen to real-time edit session updates for a document
 * @param {string} documentId - Document ID
 * @param {function} callback - Callback function to handle updates
 * @returns {function} - Unsubscribe function
 */
export const subscribeToEditSessions = (documentId, callback) => {
  try {
    const sessionsQuery = query(
      collection(db, 'c3_document_edit_sessions'),
      where('documentId', '==', documentId),
      where('status', '==', 'active'),
      orderBy('startedAt', 'desc')
    );
    
    return onSnapshot(sessionsQuery, (snapshot) => {
      const sessions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter out stale sessions
      const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000);
      const activeSessions = sessions.filter(session => {
        const lastActivity = session.lastActivityAt?.toDate() || session.startedAt?.toDate();
        return lastActivity && lastActivity > fourHoursAgo;
      });
      
      callback(activeSessions);
    });
  } catch (error) {
    console.error('❌ Error subscribing to edit sessions:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Record document version with change tracking
 * @param {string} documentId - Document ID
 * @param {Object} versionData - Version information
 * @param {string} sessionId - Edit session ID (optional)
 */
export const recordDocumentVersion = async (documentId, versionData, sessionId = null) => {
  try {
    const versionRecord = {
      documentId,
      sessionId,
      version: versionData.version,
      fileName: versionData.fileName,
      fileSize: versionData.size,
      fileType: versionData.type,
      url: versionData.url,
      uploadedBy: versionData.uploadedBy,
      uploadedByUid: versionData.uploadedByUid,
      uploadedAt: serverTimestamp(),
      changes: versionData.changes || '',
      changesSummary: versionData.changesSummary || '',
      isMinorChange: versionData.isMinorChange || false,
      previousVersion: versionData.previousVersion || null,
      metadata: {
        fileSizeChange: versionData.fileSizeChange || 0,
        estimatedWordCount: versionData.estimatedWordCount || null,
        uploadMethod: versionData.uploadMethod || 'manual'
      }
    };
    
    const versionRef = await addDoc(collection(db, 'c3_document_versions'), versionRecord);
    console.log('📄 Document version recorded:', versionRef.id);
    
    // Update edit session if provided
    if (sessionId) {
      await updateEditSession(sessionId, {
        uploadedAt: serverTimestamp(),
        newVersionId: versionRef.id
      });
    }
    
    return versionRef.id;
  } catch (error) {
    console.error('❌ Error recording document version:', error);
    throw error;
  }
};

/**
 * Get version history for a document
 * @param {string} documentId - Document ID
 * @param {number} limitCount - Number of versions to return (default: 10)
 * @returns {Promise<Array>} - Array of document versions
 */
export const getDocumentVersionHistory = async (documentId, limitCount = 10) => {
  try {
    const versionsQuery = query(
      collection(db, 'c3_document_versions'),
      where('documentId', '==', documentId),
      orderBy('uploadedAt', 'desc'),
      limit(limitCount)
    );
    
    const versionsSnapshot = await getDocs(versionsQuery);
    const versions = versionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return versions;
  } catch (error) {
    console.error('❌ Error getting document version history:', error);
    return [];
  }
};

/**
 * Get collaboration status for a document
 * @param {string} documentId - Document ID
 * @returns {Promise<Object>} - Collaboration status
 */
export const getDocumentCollaborationStatus = async (documentId) => {
  try {
    const [activeSessions, recentVersions] = await Promise.all([
      getActiveEditSessions(documentId),
      getDocumentVersionHistory(documentId, 5)
    ]);
    
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    
    // Find recent activity
    const recentActivity = recentVersions.filter(version => {
      const uploadedAt = version.uploadedAt?.toDate();
      return uploadedAt && uploadedAt > oneHourAgo;
    });
    
    return {
      isBeingEdited: activeSessions.length > 0,
      activeEditors: activeSessions.map(session => ({
        userId: session.userId,
        userName: session.userName,
        userEmail: session.userEmail,
        startedAt: session.startedAt?.toDate(),
        lastActivity: session.lastActivityAt?.toDate()
      })),
      hasRecentActivity: recentActivity.length > 0,
      recentVersions: recentActivity,
      lastModified: recentVersions.length > 0 ? recentVersions[0].uploadedAt?.toDate() : null,
      totalActiveSessions: activeSessions.length
    };
  } catch (error) {
    console.error('❌ Error getting collaboration status:', error);
    return {
      isBeingEdited: false,
      activeEditors: [],
      hasRecentActivity: false,
      recentVersions: [],
      lastModified: null,
      totalActiveSessions: 0
    };
  }
};

/**
 * Cleanup old edit sessions and versions
 * @param {number} daysOld - Days old to consider for cleanup (default: 30)
 */
export const cleanupOldSessions = async (daysOld = 30) => {
  try {
    const cutoffDate = new Date(Date.now() - daysOld * 24 * 60 * 60 * 1000);
    
    // Cleanup old completed sessions
    const oldSessionsQuery = query(
      collection(db, 'c3_document_edit_sessions'),
      where('status', '==', 'completed'),
      where('endedAt', '<', cutoffDate)
    );
    
    const oldSessions = await getDocs(oldSessionsQuery);
    console.log(`🧹 Cleaning up ${oldSessions.docs.length} old edit sessions`);
    
    for (const sessionDoc of oldSessions.docs) {
      await deleteDoc(sessionDoc.ref);
    }
    
    console.log('✅ Cleanup completed');
  } catch (error) {
    console.error('❌ Error during cleanup:', error);
  }
};

/**
 * Generate smart download URL with session tracking
 * @param {Object} document - Document object
 * @param {string} sessionId - Edit session ID
 * @returns {Object} - Enhanced download information
 */
export const generateSmartDownloadUrl = (document, sessionId) => {
  try {
    // Create enhanced download URL with session tracking
    const downloadUrl = document.url;
    const downloadInfo = {
      url: downloadUrl,
      sessionId,
      documentId: document.id,
      documentName: document.name,
      downloadedAt: new Date().toISOString(),
      instructions: {
        step1: "File will download and open in your desktop Office application",
        step2: "Make your changes and save the file",
        step3: "Return to Crisis Compass and upload the new version",
        step4: "Your changes will be automatically tracked and versioned"
      },
      estimatedEditTime: "15-30 minutes",
      reminderTime: 30 * 60 * 1000 // 30 minutes
    };
    
    return downloadInfo;
  } catch (error) {
    console.error('❌ Error generating smart download URL:', error);
    throw error;
  }
};

export default {
  createEditSession,
  updateEditSession,
  completeEditSession,
  getActiveEditSessions,
  subscribeToEditSessions,
  recordDocumentVersion,
  getDocumentVersionHistory,
  getDocumentCollaborationStatus,
  cleanupOldSessions,
  generateSmartDownloadUrl
}; 